import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { ValidationError } from '@lender/utils';
import AutocompleteCDS, {
  onAutocompleteChangeInForm,
} from '@sharedCR/coreDesignSystem/form/AutocompleteCDS';
import ModalFormWrapper, { ModalFormLayout } from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import { subCompaniesApi } from 'app/services/api';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';

import { selectMasterSubCompanyId } from 'store/auth/selectors';
import { closeDialogAction } from 'store/global/actions';
import {
  createLoanRoleAssignmentActionV2,
  getLoanRolesActionV2,
  updateLoanRoleAssignmentActionV2,
} from 'store/system/actions';
import { selectLoanRolesOptionsV2 } from 'store/system/selectors';

import { PropertyAndLoanTestIds } from '../../../../../PropertyAndLoan.testIds';

import { mapSubCompanyUsersToOptions } from './LoanRoleAssignmentForm.utils';

const defaultArray = [];

const schema = object().shape({
  loanRole: object().required(ValidationError.RequiredField).nullable(),
  user: object().required(ValidationError.RequiredField).nullable(),
});

export const ASSIGNMENT_FORM_TYPE = {
  ASSIGN: 'assign',
  REASSIGN: 'reassign',
};

const LoanRoleAssignmentForm = ({
  loanRolesToExclude = defaultArray,
  isLoading,
  setIsLoading,
  dialogId,
  onAssignLoanRole,
  onReassignLoanRole,
  type,
  loanRoleId,
  userName,
  currentUserId = null,
  isBroker = false,
  allowedLoanRoleIds = null,
}) => {
  const dispatch = useDispatch();

  const subCompanyId = useSelector(selectMasterSubCompanyId);

  const [isError, setIsError] = useState(false);
  // DEV-4720: a broker must staff from its OWN users (server-scoped), never the company-wide
  // potentialUsers list off the loan-role catalog — which is the unscoped-employee-list bug.
  const [brokerUserOptions, setBrokerUserOptions] = useState(defaultArray);

  const formRef = useRef(null);

  const allLoanRolesOptions = useSelector(selectLoanRolesOptionsV2(loanRolesToExclude));

  const loanRolesOptions = useMemo(
    () =>
      isBroker && allowedLoanRoleIds
        ? allLoanRolesOptions.filter(option => allowedLoanRoleIds.includes(option.value))
        : allLoanRolesOptions,
    [isBroker, allowedLoanRoleIds, allLoanRolesOptions],
  );

  const initialFormValues = useMemo(() => {
    const loanRole =
      type === ASSIGNMENT_FORM_TYPE.ASSIGN
        ? null
        : loanRolesOptions?.find(({ value }) => value === loanRoleId);
    const user = loanRole?.potentialUsers?.find(({ name }) => name === userName) ?? null;

    return {
      loanRole,
      user,
    };
  }, [type, loanRoleId, loanRolesOptions, userName]);

  const {
    form,
    isFormChanged,
    validationErrors,
    setSchema,
    setInForm,
    setMultipleInForm,
    setValidationErrors,
    validateField,
    validateForm,
  } = useForm(initialFormValues);

  useEffect(() => {
    setMultipleInForm(
      { loanRole: initialFormValues.loanRole, user: initialFormValues.user },
      false,
    );
  }, [initialFormValues, setMultipleInForm]);

  const usersOptions = isBroker ? brokerUserOptions : form.loanRole?.potentialUsers;

  const fetchData = useCallback(() => {
    setIsLoading(true);
    setIsError(false);

    // A broker is forbidden from the company-wide PotentialUsers employee list (403); it staffs from
    // its own sub-company users, so fetch the role catalog without that include.
    dispatch(getLoanRolesActionV2(isBroker ? { includes: null } : {}))
      .catch(() => setIsError(true))
      .finally(() => setIsLoading(false));
  }, [dispatch, isBroker, setIsLoading]);

  useEffectSafeStrictMode(() => {
    if (!isBroker || !subCompanyId) {
      return;
    }

    subCompaniesApi
      .getSubCompanyUsers({ subCompanyId, includeDescendants: true })
      .then(({ data }) => setBrokerUserOptions(mapSubCompanyUsersToOptions(data)))
      .catch(() => setBrokerUserOptions(defaultArray));
  }, [isBroker, subCompanyId]);

  useEffectSafeStrictMode(() => {
    if (allLoanRolesOptions.length) {
      return;
    }

    fetchData();
  }, [fetchData, allLoanRolesOptions.length]);

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    if (!form.user || usersOptions?.find(({ value }) => form.user.value === value)) {
      return;
    }

    setInForm('user', null);
    setValidationErrors(prev => _.omit(prev, 'user'));
  }, [form.user, usersOptions, setValidationErrors, setInForm]);

  const onClose = useCallback(() => {
    dispatch(closeDialogAction(dialogId));
  }, [dispatch, dialogId]);

  const onSubmit = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }

    const { user, loanRole } = formRef.current;
    const isAssign = type === ASSIGNMENT_FORM_TYPE.ASSIGN;

    const assignmentData = {
      userName: user.name,
      userId: user.value,
      loanRoleId: loanRole.value,
      loanRoleName: loanRole.name,
    };

    // DEV-4826: a broker staffs pre-pricing, so persist the assignment through the dedicated
    // comp-free facade endpoint instead of staging it into the comp-validated loan-info commit
    // (which 409s on an unpriced loan). The employee flow keeps the unified pending-changes commit.
    if (isBroker) {
      const saveAction = isAssign
        ? createLoanRoleAssignmentActionV2({ loanRoleId: loanRole.value, userId: user.value })
        : updateLoanRoleAssignmentActionV2({
            loanRoleId,
            userId: currentUserId,
            newUserId: user.value,
          });

      const isSaved = await dispatch(saveAction).then(
        () => true,
        () => false,
      );

      if (!isSaved) {
        return;
      }
    }

    if (isAssign) {
      onAssignLoanRole(assignmentData);
    }

    if (!isAssign) {
      onReassignLoanRole(assignmentData);
    }

    onClose();
  }, [
    onClose,
    validateForm,
    onAssignLoanRole,
    onReassignLoanRole,
    type,
    isBroker,
    dispatch,
    loanRoleId,
    currentUserId,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <ModalFormWrapper
      fetchData={fetchData}
      isError={isError}
      isFormChanged={isFormChanged}
      isSubmitButtonDisabled={isLoading}
      layout={ModalFormLayout.EMBEDDED}
      submitButtonName="Select"
      onClose={onClose}
      onSubmit={onSubmit}
    >
      <AutocompleteCDS
        required
        dataTestId={PropertyAndLoanTestIds.LOAN_ROLE_ASSIGNMENT_FORM_ROLE_SELECT}
        disabled={isLoading || type === ASSIGNMENT_FORM_TYPE.REASSIGN}
        error={!!validationErrors.loanRole}
        helperText={validationErrors.loanRole}
        label="Loan Role"
        name="loanRole"
        options={loanRolesOptions}
        placeholder="Select one"
        value={form.loanRole}
        onChange={onAutocompleteChange}
      />
      <AutocompleteCDS
        required
        dataTestId={PropertyAndLoanTestIds.LOAN_ROLE_ASSIGNMENT_FORM_USER_SELECT}
        disabled={isLoading || !form.loanRole}
        error={!!validationErrors.user}
        helperText={validationErrors.user}
        label="User Name"
        name="user"
        options={usersOptions}
        placeholder="Select one"
        value={form.user}
        onChange={onAutocompleteChange}
      />
    </ModalFormWrapper>
  );
};

LoanRoleAssignmentForm.propTypes = {
  ...WithLoadingPropTypes,
  dialogId: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(ASSIGNMENT_FORM_TYPE)).isRequired,
  loanRolesToExclude: PropTypes.array,
  currentUserId: PropTypes.string,
  isBroker: PropTypes.bool,
  allowedLoanRoleIds: PropTypes.array,
  onAssignLoanRole: PropTypes.func,
  onReassignLoanRole: PropTypes.func,
};

export default withLoading(LoanRoleAssignmentForm);
